
import { Component, Vue, Prop } from "vue-property-decorator";
import { plainToInstance } from "class-transformer";
import { CalendarTimestamp } from "vuetify";

import subscription_routes from "@/api/routes/subscriptions";
import user_routes from "@/api/routes/users";
import unit_routes from "@/api/routes/units";

import CCalendar from "@/components/calendar/Calendar.vue";
import Preloader from "@/components/Preloader.vue";
import DateTimePicker from "@/components/DateTimePicker.vue";

import UserUser from "@/models/user/user";
import SubscriptionUnit from "@/models/subscriptions/unit";
import EventVersion from "@/models/unit/unitable/event/version";
import { EUnitableType } from "@/models/unit/unit_version";

@Component({
  name: "VSubscriptionTrainerCalendar",
  components: {
    Preloader,
    CCalendar,
    DateTimePicker
  }
})
export default class VSubscriptionTrainerCalendar extends Vue {
  @Prop({ required: true }) public id!: string;
  @Prop({ required: true }) public unit_id!: string;

  public preload: boolean = false;

  private current_user: IJWTUser | null = this.$store.state.currentUser;
  private watchers: Function[] = [];

  public subscription_unit: SubscriptionUnit | null = null;
  public event_version: EventVersion | null = null;
  public trainer: UserUser | null = null;

  public starts_at: Date | null = null;
  public ends_at: Date | null = null;

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    await this.loadSubscriptionUnit();
  }

  public dayClick(data: CalendarTimestamp) {
    this.setDate(data.date);

    this.showApprovalModal();
  }

  public timeClick(data: CalendarTimestamp) {
    this.setDate(data.date);

    this.showApprovalModal();
  }

  private setDate(date: Date | string) {
    this.starts_at = date instanceof Date ? date : new Date(date);

    const duration =
      this.starts_at.getMinutes() + (this.event_version?.duration ?? 0);

    this.ends_at = new Date(this.starts_at);
    this.ends_at.setMinutes(duration);
  }

  private canUseStartsAt() {
    const current_date = new Date();

    if (!this.starts_at) {
      return false;
    }

    return this.starts_at > current_date;
  }

  public async showApprovalModal() {
    this.$modal.show("event_approval_modal");
  }

  public hideApprovalModal() {
    this.$modal.hide("event_approval_modal");
  }

  public async sendApproval() {
    if (!this.event_version) {
      return;
    }

    if (!this.canUseStartsAt()) {
      return this.$notify({
        group: "notifications",
        type: "error",
        text: "Дата начала обучения не должна быть меньше текущей даты",
        speed: 500
      });
    }

    return this.$api
      .post(unit_routes.event_results, {
        event_at: this.starts_at,
        event_id: this.event_version.event_id,
        subscription_unit_id: this.unit_id
      })
      .then(() => {
        this.$modal.hide("event_approval_modal");

        this.$store.commit("reloadCalendarEvents", true);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  private async loadSubscriptionUnit() {
    this.preload = true;

    return this.$api
      .get(subscription_routes.subscription_unit(this.unit_id), {
        params: {
          units: true,
          results: true
        }
      })
      .then(({ data: res }: { data: SubscriptionUnit }) => {
        this.subscription_unit = plainToInstance(SubscriptionUnit, res);

        if (
          this.subscription_unit.unit_version.unitable_type ===
          EUnitableType["Event::Version"]
        ) {
          this.event_version = this.subscription_unit.unit_version
            .unitable as EventVersion;
        }
      })
      .then(async () => {
        if (this.event_version) {
          await this.loadUser(this.event_version.trainer_id);
        }
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload = false;
      });
  }

  private async loadUser(user_id: string) {
    return this.$api
      .get(user_routes.user(user_id))
      .then(({ data: res }: { data: UserUser }) => {
        this.trainer = res;
      });
  }
}
